import { LocalizedError } from '@/lib/i18n/errors';
import { chatUUID, readChatPage } from './creation-contract';
import type { Channel, ChannelMember } from './queries';
export type ChannelOperation =
  | 'update'
  | 'delete'
  | 'leave'
  | 'archive'
  | 'unarchive'
  | 'mute'
  | 'unmute'
  | 'add_members'
  | 'remove_member';
export type ChannelActionInput = {
  channelId: string;
  patch?: {
    name?: string;
    description?: string;
    avatar?: string;
    is_private?: boolean;
  };
  userIds?: string[];
  userId?: string;
};
export type ChannelActionSnapshot = ChannelActionInput & {
  operation: ChannelOperation;
};
export type ChannelActionReceipt = {
  channel?: Channel;
  deleted: boolean;
  left: boolean;
};
const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const statuses: Record<string, number> = {
  unauthorized: 401,
  bad_input: 400,
  denied: 403,
  owner_only: 403,
  owner_leave: 400,
  members_fixed: 400,
  owner_protected: 400,
  not_available: 404,
  unavailable: 503,
  unconfirmed: 500,
};
export class ChannelActionError extends LocalizedError {
  constructor(
    key = 'unconfirmed',
    readonly uncertain = true,
  ) {
    super('teamchat', 'admin.problem.' + key);
  }
}
export function managementProblem(
  value: unknown,
  status: number,
): ChannelActionError {
  if (record(value) && value.success === false && record(value.error)) {
    const error = value.error;
    let key =
      typeof error.code === 'string'
        ? error.code.replace(/^chat_channel_manage_/, '')
        : '';
    if (
      typeof error.code === 'string' &&
      error.code.startsWith('chat_channel_access_')
    )
      key =
        (
          {
            unauthorized: 'unauthorized',
            bad_id: 'bad_input',
            denied: 'denied',
            unavailable: 'unavailable',
          } as Record<string, string>
        )[error.code.slice(20)] ?? '';
    if (
      (String(error.code).startsWith('chat_channel_manage_') ||
        String(error.code).startsWith('chat_channel_access_')) &&
      statuses[key] === status &&
      error.status === status &&
      error.may_have_changed === (key === 'unconfirmed')
    )
      return new ChannelActionError(key, key === 'unconfirmed');
  }
  return new ChannelActionError();
}
export function snapshotChannelAction(
  op: ChannelOperation,
  input: ChannelActionInput,
): ChannelActionSnapshot {
  if (
    ![
      'update',
      'delete',
      'leave',
      'archive',
      'unarchive',
      'mute',
      'unmute',
      'add_members',
      'remove_member',
    ].includes(op) ||
    !chatUUID(input.channelId)
  )
    throw new ChannelActionError('bad_input', false);
  const snapshot: ChannelActionSnapshot = {
    operation: op,
    channelId: input.channelId,
  };
  if (op === 'update') {
    const patch = input.patch;
    if (
      !patch ||
      !Object.keys(patch).length ||
      Object.keys(patch).some(
        (k) => !['name', 'description', 'avatar', 'is_private'].includes(k),
      )
    )
      throw new ChannelActionError('bad_input', false);
    const copy: NonNullable<ChannelActionInput['patch']> = {};
    for (const key of ['name', 'description', 'avatar'] as const) {
      const v = patch[key];
      if (v !== undefined) {
        if (
          typeof v !== 'string' ||
          v.includes('\0') ||
          [...v].some(
            (c) =>
              c.length === 1 &&
              c.charCodeAt(0) >= 0xd800 &&
              c.charCodeAt(0) <= 0xdfff,
          )
        )
          throw new ChannelActionError('bad_input', false);
        copy[key] = v;
      }
    }
    if (
      copy.name !== undefined &&
      (!copy.name.trim() || [...copy.name].length > 255)
    )
      throw new ChannelActionError('bad_input', false);
    if (patch.is_private !== undefined) {
      if (typeof patch.is_private !== 'boolean')
        throw new ChannelActionError('bad_input', false);
      copy.is_private = patch.is_private;
    }
    snapshot.patch = Object.freeze(copy);
  }
  if (op === 'add_members') {
    if (
      !input.userIds?.length ||
      !input.userIds.every(chatUUID) ||
      new Set(input.userIds).size !== input.userIds.length
    )
      throw new ChannelActionError('bad_input', false);
    snapshot.userIds = Object.freeze([...input.userIds]) as unknown as string[];
  }
  if (op === 'remove_member') {
    if (!chatUUID(input.userId))
      throw new ChannelActionError('bad_input', false);
    snapshot.userId = input.userId;
  }
  return Object.freeze(snapshot);
}
export function readChannelAction(
  value: unknown,
  owner: { tenant: string; user?: string },
  input: ChannelActionSnapshot,
): ChannelActionReceipt {
  if (
    !record(value) ||
    value.success !== true ||
    !record(value.meta) ||
    !record(value.data) ||
    value.meta.tenant_id !== owner.tenant ||
    value.meta.user_id !== owner.user ||
    value.meta.channel_id !== input.channelId ||
    value.meta.operation !== input.operation ||
    typeof value.data.deleted !== 'boolean' ||
    typeof value.data.left !== 'boolean'
  )
    throw new ChannelActionError();
  const { data } = value,
    op = input.operation;
  if (op === 'delete') {
    if (!data.deleted || data.left || data.channel !== undefined)
      throw new ChannelActionError();
    return { deleted: true, left: false };
  }
  if (op === 'leave') {
    if (!data.left || data.channel !== undefined)
      throw new ChannelActionError();
    return { deleted: data.deleted as boolean, left: true };
  }
  if (data.deleted || data.left) throw new ChannelActionError();
  let channel: Channel;
  try {
    channel = readChatPage({ success: true, data: [data.channel] }, owner)[0];
  } catch {
    throw new ChannelActionError();
  }
  if (channel.id !== input.channelId) throw new ChannelActionError();
  if (op === 'update')
    for (const [key, v] of Object.entries(input.patch!)) {
      if (channel[key as keyof Channel] !== v) throw new ChannelActionError();
    }
  if (
    (op === 'archive' || op === 'unarchive') &&
    channel.is_archived !== (op === 'archive')
  )
    throw new ChannelActionError();
  if (
    (op === 'mute' || op === 'unmute') &&
    channel.members?.find((m) => m.user_id === owner.user)?.is_muted !==
      (op === 'mute')
  )
    throw new ChannelActionError();
  if (
    op === 'add_members' &&
    !input.userIds!.every((id) =>
      channel.members?.some((m) => m.user_id === id),
    )
  )
    throw new ChannelActionError();
  if (
    op === 'remove_member' &&
    channel.members?.some((m) => m.user_id === input.userId)
  )
    throw new ChannelActionError();
  return { channel, deleted: false, left: false };
}
export function readOwnedChannelMembers(
  value: unknown,
  owner: { tenant: string; user?: string },
  channel: string,
): ChannelMember[] {
  if (
    !record(value) ||
    value.success !== true ||
    !Array.isArray(value.data) ||
    !chatUUID(owner.tenant) ||
    !chatUUID(owner.user) ||
    !chatUUID(channel)
  )
    throw new ChannelActionError('members_failed', false);
  const rows: ChannelMember[] = value.data.map((m) => {
    if (
      !record(m) ||
      !chatUUID(m.id) ||
      m.channel_id !== channel ||
      !chatUUID(m.user_id) ||
      typeof m.role !== 'string' ||
      typeof m.is_muted !== 'boolean' ||
      typeof m.joined_at !== 'string' ||
      !Number.isFinite(Date.parse(m.joined_at)) ||
      (m.last_read_at !== undefined &&
        (typeof m.last_read_at !== 'string' ||
          !Number.isFinite(Date.parse(m.last_read_at)))) ||
      (m.user_name !== undefined && typeof m.user_name !== 'string') ||
      (m.user_avatar !== undefined && typeof m.user_avatar !== 'string')
    )
      throw new ChannelActionError('members_failed', false);
    return {
      id: m.id,
      channel_id: channel,
      user_id: m.user_id,
      user_name: m.user_name as string | undefined,
      user_avatar: m.user_avatar as string | undefined,
      role: m.role,
      is_muted: m.is_muted,
      joined_at: m.joined_at,
      last_read_at: m.last_read_at as string | undefined,
    };
  });
  if (
    !rows.some((m) => m.user_id === owner.user) ||
    new Set(rows.map((m) => m.id)).size !== rows.length ||
    new Set(rows.map((m) => m.user_id)).size !== rows.length
  )
    throw new ChannelActionError('members_failed', false);
  return rows;
}
